import type { AnchorHTMLAttributes, ButtonHTMLAttributes, Ref } from 'react';
import { forwardRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import type { IconName, IconSize, IconVariant } from '~/chords/Icon';
import { Icon } from '~/chords/Icon';
import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

type Size = 'xlarge' | 'large' | 'medium' | 'small';

const getButtonVariants = variants({
  base: cn([
    'inline-block',
    'leading-none',
    'font-medium',
    'flex items-center justify-center',
    'rounded-full',
    'bg-transparent',
    'duration-300 ease-out',
    'enabled:hover:bg-emphasis-strong',
    'enabled:active:bg-primary-default',
    'disabled:cursor-not-allowed disabled:text-icon-disabled',
  ]),
  variants: {
    size: {
      xlarge: cn('size-[3rem] p-md'),
      large: cn('size-2xl p-sm'),
      medium: cn('size-xl p-sm'),
      small: cn('size-[1.75rem] p-xs'),
    },
  },
});

interface BaseProps {
  iconType: IconName;
  iconVariant?: IconVariant;
  iconVariantHover?: IconVariant;
  size?: Size;
}

type ButtonProps = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    asLink?: false;
    linkTo?: undefined;
  };

type LinkProps = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    asLink: true;
    linkTo: string;
  };

type Props = ComponentProps<ButtonProps | LinkProps>;

const ICON_SIZE: Record<Size, IconSize> = {
  xlarge: 'lg',
  large: 'lg',
  medium: 'md',
  small: 'sm',
};

export const IconButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  Props
>(
  (
    {
      iconType,
      size = 'medium',
      iconVariant = 'solid',
      iconVariantHover,
      dangerouslyOverrideClassName,
      asLink,
      linkTo,
      ...props
    }: Props,
    ref: Ref<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const className = getClassNameOverride(
      { dangerouslyOverrideClassName },
      'IconButton',
    );
    const styles = getButtonVariants({ size });
    const [isHovered, setIsHovered] = useState(false);
    const iconParams = {
      type: iconType,
      variant: iconVariantHover && isHovered ? iconVariantHover : iconVariant,
      size: ICON_SIZE[size],
    };

    if (asLink && linkTo?.trim().length) {
      return (
        <Link
          {...(props as LinkProps)}
          className={cn(styles, className)}
          to={linkTo}
          ref={ref as Ref<HTMLAnchorElement>}
        >
          <Icon {...iconParams} />
        </Link>
      );
    }

    return (
      <button
        {...(props as ButtonProps)}
        className={cn(styles, className)}
        ref={ref as Ref<HTMLButtonElement>}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Icon {...iconParams} />
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';
